import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "@tanstack/react-router";
import { lastListSearch } from "./list-view";
import { useForm, type FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Check, CheckCircle2, ChevronDown, X } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { cn } from "../../lib/utils";
import { toApiFailure } from "../../lib/errors";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Card,
  CardContent,
} from "../../components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  useCreateIncident,
  type CreateIncidentRequest,
  type IncidentResponse,
} from "./api";
import { useActiveMasterData } from "../master-data/api";
import { activeUsers, useUsers } from "../users/api";
import { canCreate } from "./permissions";
import { useInputDraft } from "../drafts/use-input-draft";
import { DraftRestoreBanner, DraftSaveStatusLine } from "../drafts/draft-banner";
import type { DraftPayload } from "../drafts/draft-buffer";
import { readString, readStrings, samePayload, toDraftPayload } from "../drafts/payload";

/** Explicit form value shape (mirrors CreateIncidentRequest for this slice). */
interface CreateFormValues {
  title: string;
  description: string;
  categoryId: string;
  severityId: string;
  discoveredAt: string;
  detectedVia: string;
  reportedById: string;
  areaIds: string[];
  tagIds: string[];
}

/** The mask's fields as [NF-022] keeps them. */
const DRAFT_FIELDS = [
  "title",
  "description",
  "categoryId",
  "severityId",
  "discoveredAt",
  "detectedVia",
  "reportedById",
  "areaIds",
  "tagIds",
] as const satisfies readonly (keyof CreateFormValues)[];

/** A stored draft back into the mask; whatever it lacks keeps the fresh value. */
function fromDraft(payload: DraftPayload, fresh: CreateFormValues): CreateFormValues {
  return {
    title: readString(payload, "title", fresh.title),
    description: readString(payload, "description", fresh.description),
    categoryId: readString(payload, "categoryId", fresh.categoryId),
    severityId: readString(payload, "severityId", fresh.severityId),
    discoveredAt: readString(payload, "discoveredAt", fresh.discoveredAt),
    detectedVia: readString(payload, "detectedVia", fresh.detectedVia),
    reportedById: readString(payload, "reportedById", fresh.reportedById),
    areaIds: readStrings(payload, "areaIds"),
    tagIds: readStrings(payload, "tagIds"),
  };
}

/** Local `datetime-local` value (YYYY-MM-DDTHH:mm) for the quick-set buttons. */
function toLocalDateTimeValue(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** Red asterisk marking a currently-required field (M-004); decorative. */
function RequiredMark(): JSX.Element {
  return (
    <span aria-hidden="true" className="text-destructive-text">
      *
    </span>
  );
}

export function IncidentCreatePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Picker offers ACTIVE-ONLY master data (F-009 / AC-010): a since-deactivated
  // entry must never be selectable on a new incident. Name resolution on
  // existing incidents keeps using the full lists (useMasterData) elsewhere.
  const master = useActiveMasterData();
  // Active directory for the "gemeldet von" override ([F-002]). Same source the
  // detail screen's owner/assignee pickers use.
  const usersQuery = useUsers();
  const create = useCreateIncident();

  /**
   * Options for "gemeldet von", with the logged-in user guaranteed present.
   *
   * The form is prefilled with their id at mount, but `/users` resolves a moment
   * later - without this the Select would render blank over a value the form
   * already holds, which reads as "the Pflichtfeld is empty" when it is not.
   */
  const reporterOptions = useMemo(() => {
    const list = activeUsers(usersQuery.data).map((entry) => ({
      id: entry.id,
      displayName: entry.displayName,
    }));
    if (user?.userId && !list.some((entry) => entry.id === user.userId)) {
      return [{ id: user.userId, displayName: user.displayName }, ...list];
    }
    return list;
  }, [usersQuery.data, user]);

  // Post-submit view state: the incident just created (the notice bar above
  // the fresh form) and the inline save-error banner.
  const [created, setCreated] = useState<IncidentResponse | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Tags live behind the collapsed "Mehr Details" disclosure (M-004).
  const [detailsOpen, setDetailsOpen] = useState(false);

  const schema = useMemo(
    () =>
      z.object({
        title: z
          .string()
          .trim()
          .min(1, t("create.validation.titleRequired"))
          .max(200, t("create.validation.titleMax")),
        description: z
          .string()
          .trim()
          .min(1, t("create.validation.descriptionRequired")),
        categoryId: z.string().min(1, t("create.validation.categoryRequired")),
        severityId: z.string().min(1, t("create.validation.severityRequired")),
        discoveredAt: z
          .string()
          .min(1, t("create.validation.discoveredAtRequired")),
        detectedVia: z
          .string()
          .trim()
          .min(1, t("create.validation.detectedViaRequired")),
        // [F-002] is explicit that a prefill does not weaken the [F-001]
        // Pflichtfeld check: clearing a prefilled field without a replacement
        // must still refuse the save.
        reportedById: z
          .string()
          .min(1, t("create.validation.reportedByRequired")),
        areaIds: z.array(z.string()).min(1, t("create.validation.areasRequired")),
        tagIds: z.array(z.string()),
      }),
    [t],
  );

  // One source for the initial form and the reset after a create (CD-003),
  // so the prefills and the current time are taken afresh each time.
  const emptyValues = (): CreateFormValues => ({
    title: "",
    description: "",
    categoryId: "",
    severityId: "",
    discoveredAt: toLocalDateTimeValue(new Date()),
    detectedVia: user?.displayName ?? "",
    reportedById: user?.userId ?? "",
    areaIds: [],
    tagIds: [],
  });

  const form = useForm<CreateFormValues>({
    resolver: zodResolver(schema),
    // [F-002]/[AC-002]: three Pflichtfelder arrive prefilled and every one of
    // them stays overridable - "gemeldet von" and "erkannt durch" with the
    // logged-in user, "Entdeckungszeitpunkt" with the current time. None of the
    // three existed before (review lean_v0-8 / PO-018), which also made the
    // [NF-011] 60-second measurement stricter than the SRS describes, because
    // the tester had to type all three by hand.
    //
    // Reading `user` here is safe: App.tsx only mounts the router in the
    // authenticated branch, so this component never renders without a session.
    defaultValues: emptyValues(),
  });

  // [NF-022] / [AC-030]: the mask is saved as the user types, without being
  // asked, and an unsaved state found on arrival is offered back. Only what
  // differs from the fresh mask is worth keeping - the prefills alone are not
  // input. The draft never becomes an incident; the incident list does not
  // know it exists.
  const draft = useInputDraft({
    form: "incident_create",
    context: "",
    userId: user?.userId,
    enabled: canCreate(user),
  });
  const scheduleDraft = draft.schedule;
  const baseline = useRef<DraftPayload>(toDraftPayload({ ...form.getValues() }, DRAFT_FIELDS));
  useEffect(() => {
    const subscription = form.watch((values) => {
      const payload = toDraftPayload({ ...values }, DRAFT_FIELDS);
      scheduleDraft(samePayload(payload, baseline.current) ? null : payload);
    });
    return () => subscription.unsubscribe();
  }, [form, scheduleDraft]);

  const restoreDraft = (): void => {
    const payload = draft.restore();
    if (!payload) return;
    const restored = fromDraft(payload, emptyValues());
    form.reset(restored);
    if (restored.tagIds.length > 0) setDetailsOpen(true);
  };

  if (!canCreate(user)) {
    return (
      <div
        data-testid="incident-create_forbidden"
        className="rounded-lg border border-dashed px-6 py-16 text-center text-muted-foreground"
      >
        {t("errors.forbidden")}
      </div>
    );
  }

  const categories = master.categories.data ?? [];
  const severities = master.severities.data ?? [];
  const areas = master.areas.data ?? [];
  const tags = master.tags.data ?? [];

  const submit = (values: CreateFormValues): void => {
    setSaveError(null);
    const body: CreateIncidentRequest = {
      title: values.title,
      description: values.description,
      categoryId: values.categoryId,
      severityId: values.severityId,
      discoveredAt: new Date(values.discoveredAt).toISOString(),
      detectedVia: values.detectedVia,
      reportedById: values.reportedById,
      areaIds: values.areaIds,
      ...(values.tagIds.length > 0 ? { tagIds: values.tagIds } : {}),
    };
    create.mutate(body, {
      // CD-003 (LP-024): no confirmation screen - back to an empty form, with a
      // notice bar linking the new Vorfall, because whoever records several in
      // a row wants the form. The prefills are taken again, the
      // Entdeckungszeitpunkt with the current time rather than the page load.
      onSuccess: (createdIncident) => {
        setCreated(createdIncident);
        setDetailsOpen(false);
        const fresh = emptyValues();
        baseline.current = toDraftPayload({ ...fresh }, DRAFT_FIELDS);
        form.reset(fresh);
        // The server dropped the draft with the create; this also stops an
        // autosave that was still queued or in flight.
        void draft.clear();
        window.scrollTo({ top: 0 });
        form.setFocus("title");
      },
      onError: (error) => {
        // Inline banner instead of a toast; keep the RFC 7807 detail if any.
        const failure = toApiFailure(error);
        setSaveError(failure.problem?.detail ?? t("errors.saveFailed"));
      },
    });
  };

  const onInvalid = (errors: FieldErrors<CreateFormValues>): void => {
    const firstKey = Object.keys(errors)[0] as keyof CreateFormValues | undefined;
    if (!firstKey) return;
    try {
      // Text inputs focus; the pill/chip custom controls are a no-op here.
      form.setFocus(firstKey);
    } catch {
      /* non-focusable custom control - the summary banner still guides. */
    }
  };

  const onSubmit = form.handleSubmit(submit, onInvalid);
  const errorCount = Object.keys(form.formState.errors).length;
  const showValidationSummary = form.formState.submitCount > 0 && errorCount > 0;

  const setDiscovered = (date: Date): void =>
    form.setValue("discoveredAt", toLocalDateTimeValue(date), {
      shouldValidate: true,
      shouldDirty: true,
    });

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="space-y-1">
        <h1
          data-testid="incident-create_heading"
          className="text-2xl font-semibold tracking-tight"
        >
          {t("create.title")}
        </h1>
        <p
          data-testid="incident-create_subtitle"
          className="text-sm text-muted-foreground"
        >
          {t("create.subtitle")}
        </p>
      </div>

      {created ? (
        <div
          role="status"
          data-testid="incident-create_created-notice"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 px-4 py-3 text-sm text-emerald-900 dark:text-emerald-100"
        >
          <span className="flex items-center gap-2">
            <CheckCircle2 aria-hidden="true" className="h-4 w-4 shrink-0" />
            {t("create.createdNotice", { number: created.number })}
          </span>
          <span className="flex items-center gap-3">
            <Link
              to="/incidents/$id"
              params={{ id: created.id }}
              data-testid="incident-create_created-open"
              className="font-medium underline underline-offset-4"
            >
              {t("create.successOpen")}
            </Link>
            <button
              type="button"
              data-testid="incident-create_created-dismiss"
              aria-label={t("create.createdDismiss")}
              onClick={() => setCreated(null)}
              className="rounded-sm p-0.5 hover:bg-emerald-100"
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </button>
          </span>
        </div>
      ) : null}

      {draft.offered ? (
        <DraftRestoreBanner
          mask="incident-create"
          offered={draft.offered}
          onRestore={restoreDraft}
          onDiscard={draft.dismiss}
        />
      ) : null}

      <Form {...form}>
        <form
          data-testid="incident-create_form"
          onSubmit={onSubmit}
          className="space-y-6"
        >
          <Card>
            <CardContent className="space-y-6 pt-6">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("create.fieldTitle")} <RequiredMark />
                    </FormLabel>
                    <FormControl>
                      <Input
                        data-testid="incident-create_title"
                        maxLength={200}
                        autoFocus
                        placeholder={t("create.titlePlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage data-testid="incident-create_title-error" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("create.fieldDescription")} <RequiredMark />
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        data-testid="incident-create_description"
                        rows={4}
                        placeholder={t("create.descriptionPlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage data-testid="incident-create_description-error" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="categoryId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("create.fieldCategory")} <RequiredMark />
                    </FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger data-testid="incident-create_category">
                          <SelectValue
                            placeholder={t("create.selectPlaceholder")}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem
                            key={category.id}
                            value={category.id}
                            data-testid={`incident-create_category-option_${category.id}`}
                          >
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage data-testid="incident-create_category-error" />
                  </FormItem>
                )}
              />

              {/* Schweregrad as a pill radiogroup (M-004): each pill carries the
                  master-data colorHint dot; selected pill is primary-styled. */}
              <FormField
                control={form.control}
                name="severityId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("create.fieldSeverity")} <RequiredMark />
                    </FormLabel>
                    <div
                      data-testid="incident-create_severity"
                      role="radiogroup"
                      aria-label={t("create.fieldSeverity")}
                      className="flex flex-wrap gap-2"
                    >
                      {severities.map((severity) => {
                        const selected = field.value === severity.id;
                        return (
                          <button
                            key={severity.id}
                            data-testid={`incident-create_severity-option_${severity.id}`}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => field.onChange(severity.id)}
                            className={cn(
                              "inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors",
                              selected
                                ? "border-primary-text bg-primary text-primary-foreground"
                                : "border-input bg-background text-foreground hover:bg-accent",
                            )}
                          >
                            {severity.colorHint ? (
                              <span
                                aria-hidden="true"
                                className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-black/10"
                                style={{ backgroundColor: severity.colorHint }}
                              />
                            ) : null}
                            {severity.name}
                          </button>
                        );
                      })}
                    </div>
                    <FormMessage data-testid="incident-create_severity-error" />
                  </FormItem>
                )}
              />

              {/* Betroffene Bereiche as toggle chips (M-004): aria-pressed,
                  Check glyph when selected, 44px touch targets. */}
              <FormField
                control={form.control}
                name="areaIds"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("create.fieldAreas")} <RequiredMark />
                    </FormLabel>
                    <div
                      data-testid="incident-create_areas"
                      className="flex flex-wrap gap-2"
                    >
                      {areas.map((area) => {
                        const selected = field.value.includes(area.id);
                        return (
                          <button
                            key={area.id}
                            data-testid={`incident-create_area-option_${area.id}`}
                            type="button"
                            aria-pressed={selected}
                            onClick={() =>
                              field.onChange(
                                selected
                                  ? field.value.filter((id) => id !== area.id)
                                  : [...field.value, area.id],
                              )
                            }
                            className={cn(
                              "inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors",
                              selected
                                ? "border-primary-text bg-primary text-primary-foreground"
                                : "border-input bg-background text-foreground hover:bg-accent",
                            )}
                          >
                            {selected ? (
                              <Check aria-hidden="true" className="h-4 w-4" />
                            ) : null}
                            {area.name}
                          </button>
                        );
                      })}
                    </div>
                    <p
                      data-testid="incident-create_areas-hint"
                      className="text-xs text-muted-foreground"
                    >
                      {t("create.areasHint")}
                    </p>
                    <FormMessage data-testid="incident-create_areas-error" />
                  </FormItem>
                )}
              />

              <div className="grid gap-6 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="discoveredAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("create.fieldDiscoveredAt")} <RequiredMark />
                      </FormLabel>
                      <FormControl>
                        <Input
                          data-testid="incident-create_discovered-at"
                          type="datetime-local"
                          {...field}
                        />
                      </FormControl>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          data-testid="incident-create_discovered-now"
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setDiscovered(new Date())}
                        >
                          {t("create.quickNow")}
                        </Button>
                        <Button
                          data-testid="incident-create_discovered-today"
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            setDiscovered(today);
                          }}
                        >
                          {t("create.quickToday")}
                        </Button>
                      </div>
                      <FormMessage data-testid="incident-create_discovered-at-error" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="detectedVia"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("create.fieldDetectedVia")} <RequiredMark />
                      </FormLabel>
                      <FormControl>
                        <Input
                          data-testid="incident-create_detected-via"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage data-testid="incident-create_detected-via-error" />
                    </FormItem>
                  )}
                />
              </div>

              {/* [F-001]'s eighth Pflichtfeld. A picker rather than a text
                  field, because it stores a user reference (DO-001) - and it
                  is overridable per [F-002] so a colleague can file on
                  someone's behalf. */}
              <FormField
                control={form.control}
                name="reportedById"
                render={({ field }) => (
                  <FormItem className="sm:max-w-72">
                    <FormLabel>
                      {t("create.fieldReportedBy")} <RequiredMark />
                    </FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger data-testid="incident-create_reported-by">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {reporterOptions.map((option) => (
                          <SelectItem
                            key={option.id}
                            value={option.id}
                            data-testid={`incident-create_reported-by-option_${option.id}`}
                          >
                            {option.displayName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p
                      data-testid="incident-create_reported-by-hint"
                      className="text-xs text-muted-foreground"
                    >
                      {t("create.reportedByHint")}
                    </p>
                    <FormMessage data-testid="incident-create_reported-by-error" />
                  </FormItem>
                )}
              />

              {/* Tags behind a collapsed "Mehr Details" disclosure (M-004);
                  nothing renders when no tags exist. */}
              {tags.length > 0 ? (
                <div className="space-y-4">
                  <button
                    data-testid="incident-create_more-details"
                    type="button"
                    aria-expanded={detailsOpen}
                    onClick={() => setDetailsOpen((open) => !open)}
                    className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
                  >
                    <ChevronDown
                      aria-hidden="true"
                      className={cn(
                        "h-4 w-4 transition-transform",
                        detailsOpen && "rotate-180",
                      )}
                    />
                    {t("create.moreDetails")}
                  </button>
                  {detailsOpen ? (
                    <FormField
                      control={form.control}
                      name="tagIds"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("create.fieldTags")}</FormLabel>
                          <div
                            data-testid="incident-create_tags"
                            className="flex flex-wrap gap-2"
                          >
                            {tags.map((tag) => {
                              const selected = field.value.includes(tag.id);
                              return (
                                <button
                                  key={tag.id}
                                  data-testid={`incident-create_tag-option_${tag.id}`}
                                  type="button"
                                  aria-pressed={selected}
                                  onClick={() =>
                                    field.onChange(
                                      selected
                                        ? field.value.filter(
                                            (id) => id !== tag.id,
                                          )
                                        : [...field.value, tag.id],
                                    )
                                  }
                                  className={cn(
                                    "inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors",
                                    selected
                                      ? "border-primary-text bg-primary text-primary-foreground"
                                      : "border-input bg-background text-foreground hover:bg-accent",
                                  )}
                                >
                                  {selected ? (
                                    <Check
                                      aria-hidden="true"
                                      className="h-4 w-4"
                                    />
                                  ) : null}
                                  {tag.name}
                                </button>
                              );
                            })}
                          </div>
                        </FormItem>
                      )}
                    />
                  ) : null}
                </div>
              ) : null}
            </CardContent>
          </Card>

          {/* The read-only "Melder" card that used to sit here is gone. It
              showed "gemeldet von" behind a lock icon with the hint
              "Automatisch aus Ihrer Anmeldung uebernommen" - which is exactly
              what [F-002] forbids: the field is prefilled with the logged-in
              user AND overridable. The editable field now lives in the form
              above; two surfaces for one Pflichtfeld, one of them claiming it
              is immutable, is the contradiction PO-018 found. */}

          {/* Validation summary (M-004): appears above the actions on a failed
              submit; focus already moved to the first invalid field. */}
          {showValidationSummary ? (
            <div
              data-testid="incident-create_validation-summary"
              role="alert"
              className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive-text"
            >
              {t("create.validationSummary", { count: errorCount })}
            </div>
          ) : null}

          {/* Save error (M-004): inline banner with a retry that re-submits. */}
          {saveError ? (
            <div
              data-testid="incident-create_save-error"
              role="alert"
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm"
            >
              <span>{saveError}</span>
              <Button
                data-testid="incident-create_save-retry"
                type="button"
                variant="outline"
                size="sm"
                disabled={create.isPending}
                onClick={() => void onSubmit()}
              >
                {t("common.tryAgain")}
              </Button>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-end gap-3">
            <div className="mr-auto">
              <DraftSaveStatusLine mask="incident-create" status={draft.status} savedAt={draft.savedAt} />
            </div>
            <Button
              data-testid="incident-create_cancel"
              type="button"
              variant="outline"
              className="h-11 sm:h-9"
              onClick={() => void navigate({ to: "/incidents", search: lastListSearch() })}
            >
              {t("common.cancel")}
            </Button>
            <Button
              data-testid="incident-create_submit"
              type="submit"
              className="h-11 sm:h-9"
              disabled={create.isPending}
            >
              {create.isPending
                ? t("create.submitting")
                : t("create.submit")}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
